import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { getMovieById, getRelated } from "@/lib/catalog";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useReveal } from "@/hooks/useReveal";

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-background-200 last:border-0">
      <span className="w-8 h-8 rounded-md bg-background-100 text-secondary-600 flex items-center justify-center shrink-0">
        <i className={`${icon} text-base`}></i>
      </span>
      <div className="min-w-0">
        <p className="text-xs text-foreground-400 font-label">{label}</p>
        <p className="text-sm text-foreground-800 mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const related = movie ? getRelated(movie, 4) : [];

  usePageSeo({
    title: movie
      ? `${movie.title} - 电影详情 - 蜜桃成熟时电影`
      : "电影信息不存在 - 蜜桃成熟时电影",
    description: movie
      ? `${movie.title}剧情介绍、上映时间、主演信息及相关推荐，${movie.year}年${movie.region}${movie.genres.join("、")}电影。`
      : "未找到该电影的信息，返回首页浏览更多电影资源与剧情介绍。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情,${movie.genres.join(",")},${movie.cast.join(",")},蜜桃成熟时电影`
      : "蜜桃成熟时电影",
  });

  useReveal([id]);

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
          <Breadcrumb items={[{ label: "电影详情" }]} />
          <div className="py-12">
            <EmptyState
              icon="ri-error-warning-line"
              title="电影信息不存在"
              desc="你访问的电影可能已被移除或链接有误，返回首页继续浏览吧。"
              action={
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              }
            />
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero backdrop */}
      <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden bg-secondary-950">
        <img
          src={movie.hero ?? movie.cover}
          alt={`${movie.title}剧照`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950 via-secondary-950/60 to-secondary-950/30"></div>
      </section>

      <div className="w-full px-4 md:px-8 -mt-16 md:-mt-20 relative z-10">
        <div className="inline-flex max-w-full bg-background-50 border border-background-200 rounded-lg px-4 py-2.5">
          <Breadcrumb items={[{ label: "电影分类", to: "/category" }, { label: movie.title }]} />
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Left: poster + basic info */}
          <div className="lg:col-span-1">
            <div className="w-[200px] md:w-[240px] aspect-[2/3] rounded-lg overflow-hidden border-4 border-background-50 bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title}海报`}
                title={`${movie.title} - 高清电影资源与剧情介绍`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6 bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
              <h2 className="font-heading font-bold text-lg text-foreground-950 mb-2">基本信息</h2>
              <div>
                <InfoRow icon="ri-user-star-line" label="导演" value={movie.director} />
                <InfoRow icon="ri-group-line" label="主演" value={movie.cast.join(" / ")} />
                <InfoRow icon="ri-price-tag-3-line" label="类型" value={movie.genres.join(" / ")} />
                <InfoRow icon="ri-calendar-line" label="上映年份" value={`${movie.year} 年`} />
                <InfoRow icon="ri-time-line" label="片长" value={movie.duration} />
                <InfoRow icon="ri-translate-2" label="语言" value={movie.language} />
                <InfoRow icon="ri-earth-line" label="地区" value={movie.region} />
              </div>
            </div>
          </div>

          {/* Right: title, synopsis, related */}
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {movie.tags.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label">
                  {t}
                </span>
              ))}
            </div>
            <h1 className="font-heading font-black text-3xl md:text-5xl text-foreground-950">{movie.title}</h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1 text-accent-600 font-label font-bold text-base">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.duration}</span>
              <span>{movie.genres.join(" / ")}</span>
            </div>

            <div className="mt-8 bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                剧情简介
              </h2>
              <p className="text-sm md:text-base leading-8 text-foreground-700">{movie.synopsis}</p>
              <p className="text-sm md:text-base leading-8 text-foreground-700 mt-4">
                《{movie.title}》由{movie.director}执导，{movie.cast.slice(0, 2).join("、")}领衔主演，
                是一部{movie.year}年的{movie.region}{movie.genres.join("、")}电影。影片以细腻的叙事与出色的表演，
                呈现出独特的影像风格，适合喜爱{movie.genres[0]}题材的观众细细品味。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-secondary-700 text-background-50 text-sm font-label font-medium hover:bg-secondary-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类页
              </Link>
              <Link
                to={`/category?genre=${encodeURIComponent(movie.genres[0])}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-700 text-sm font-label hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-price-tag-3-line"></i>
                更多{movie.genres[0]}电影
              </Link>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16 md:mt-20">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.2em] text-primary-600 uppercase mb-2">
                  <span className="w-5 h-px bg-primary-400"></span>
                  You may also like
                </span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">相关推荐</h2>
              </div>
              <Link
                to="/category"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-label text-secondary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}